import { useRef, type FormEvent } from "react";
import { Button } from "./Button";
import { TextArea, TextField } from "./TextField";
import { useControllableState } from "../utils/useControllableState";

export interface QuickCaptureValue {
  title: string;
  body: string;
}

export interface QuickCaptureProps {
  value?: QuickCaptureValue;
  defaultValue?: QuickCaptureValue;
  onValueChange?: (value: QuickCaptureValue) => void;
  onSubmit: (value: QuickCaptureValue) => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  submitting?: boolean;
  error?: string;
  maxLength?: number;
  titleLabel?: string;
  titlePlaceholder?: string;
  bodyLabel?: string;
  bodyPlaceholder?: string;
  cancelLabel?: string;
  savingLabel?: string;
  showTitle?: boolean;
}

const emptyCapture: QuickCaptureValue = { title: "", body: "" };

export function QuickCapture({
  value,
  defaultValue = emptyCapture,
  onValueChange,
  onSubmit,
  onCancel,
  submitLabel = "Save note",
  submitting = false,
  error,
  maxLength = 4000,
  titleLabel = "Title",
  titlePlaceholder = "Untitled note",
  bodyLabel = "Note",
  bodyPlaceholder = "Capture a thought, task, or link…",
  cancelLabel = "Cancel",
  savingLabel = "Saving…",
  showTitle = true,
}: QuickCaptureProps) {
  const [capture, setCapture] = useControllableState({
    value,
    defaultValue,
    onChange: onValueChange,
  });
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const isEmpty = !capture.title.trim() && !capture.body.trim();
  const primaryModifier =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/u.test(navigator.platform)
      ? "⌘"
      : "Ctrl";

  const update = (next: Partial<QuickCaptureValue>) => {
    setCapture({ ...capture, ...next });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!isEmpty && !submitting) {
      void onSubmit({
        title: capture.title.trim(),
        body: capture.body.trim(),
      });
    }
  };

  return (
    <form className="ui-quick-capture" onSubmit={handleSubmit}>
      {showTitle ? (
        <TextField
          label={titleLabel}
          value={capture.title}
          placeholder={titlePlaceholder}
          onChange={(event) => update({ title: event.currentTarget.value })}
        />
      ) : null}
      <TextArea
        ref={bodyRef}
        autoFocus
        label={bodyLabel}
        value={capture.body}
        placeholder={bodyPlaceholder}
        rows={7}
        maxLength={maxLength}
        error={error}
        onChange={(event) => update({ body: event.currentTarget.value })}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
            event.currentTarget.form?.requestSubmit();
          }
        }}
      />
      <div className="ui-quick-capture__meta">
        <span>{capture.body.length.toLocaleString()} / {maxLength.toLocaleString()}</span>
        <span>
          <kbd>{primaryModifier}</kbd><span aria-hidden="true"> + </span><kbd>Enter</kbd>
          <span className="ui-visually-hidden">
            {primaryModifier === "⌘" ? "Command" : "Control"} plus Enter
          </span>
        </span>
      </div>
      <div className="ui-quick-capture__actions">
        {onCancel ? (
          <Button variant="ghost" disabled={submitting} onClick={onCancel}>
            {cancelLabel}
          </Button>
        ) : null}
        <Button variant="primary" type="submit" disabled={isEmpty || submitting}>
          {submitting ? savingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}
